import { useMemo } from "react";
import { proxy, snapshot, subscribe, useSnapshot } from "valtio";

export type URL_SORT_CONFIG = {
  queryParamKey: string;
  column: string;
  direction: string;
};
const urlSortParamKeys = (key: string) => ({
  direction: `${key}_sort_direction`,
  sort: `${key}_sort`,
});

export const addSortToBaseURL = (baseURL: string, sort?: URL_SORT_CONFIG) => {
  if (!sort) {
    return baseURL;
  }
  const paramKeys = urlSortParamKeys(sort.queryParamKey);
  return `${baseURL}?${paramKeys.sort}=${sort.column}&${paramKeys.direction}=${sort.direction}`;
};
export class SortManager<T extends object> {
  private sort = proxy<{
    column: string;
    key: string;
    queryParamKey?: string;
    direction: "asc" | "desc";
    accessor: (row: T) => string | number;
  }>({
    column: "",
    direction: "desc",
    get key() {
      return this.column;
    },
    accessor: (row: T) => -1,
  });

  getURLConfig = () => {
    if (!this.sort.queryParamKey) return undefined;
    return {
      queryParamKey: this.sort.queryParamKey,
      column: this.sort.column,
      direction: this.sort.direction,
    };
  };

  getSnapshot = () => {
    return snapshot(this.sort);
  };

  constructor(
    initialSort?: {
      column: string;
      accessor: (row: T) => string | number;
      key: string;
      direction: "asc" | "desc";
    },
    config?: {
      queryParamKey?: string;
    },
  ) {
    if (initialSort && !config?.queryParamKey) {
      this.sort.column = initialSort.column;
      this.sort.accessor = initialSort.accessor;
      this.sort.direction = initialSort.direction;
      this.sort.key = initialSort.key;
    }
    if (config?.queryParamKey != null && typeof window !== "undefined") {
      this.sort.queryParamKey = config?.queryParamKey;
      const paramKeys = urlSortParamKeys(config?.queryParamKey);
      const queryParamSortColumnValue = new URLSearchParams(
        window.location.search,
      ).get(paramKeys.sort);
      const queryParamSortDirectionValue = new URLSearchParams(
        window.location.search,
      ).get(paramKeys.direction);

      if (queryParamSortColumnValue && queryParamSortDirectionValue) {
        this.sort.column = queryParamSortColumnValue;
        if (
          queryParamSortDirectionValue === "asc" ||
          queryParamSortDirectionValue === "desc"
        ) {
          this.sort.direction = queryParamSortDirectionValue as "asc" | "desc";
        } else {
          this.sort.direction = "desc";
        }
      } else {
        if (initialSort) {
          this.sort.column = initialSort.column;
          this.sort.accessor = initialSort.accessor;
          this.sort.direction = initialSort.direction;
          this.sort.key = initialSort.key;
        }
      }

      subscribe(this.sort, () => {
        let params = new URLSearchParams(location.search);
        params.set(paramKeys.sort, this?.sort?.column ?? "");
        params.set(paramKeys.direction, this?.sort?.direction ?? "");
        let newUrl = location.pathname + "?" + params.toString();
        history.replaceState({}, "", newUrl);
      });
    }
  }

  toggleSort = (column: string, accessor: (row: T) => string | number) => {
    this.sort.column = column;
    this.sort.accessor = accessor;
    this.sort.direction = this.sort.direction === "asc" ? "desc" : "asc";
  };

  setSort = (
    column: string,
    accessor: (row: T) => string | number,
    direction: "asc" | "desc",
  ) => {
    this.sort.column = column;
    this.sort.accessor = accessor;
    this.sort.direction = direction;
  };

  getSortDirection = (column: string) => {
    if (this.sort.column === column) {
      return this.sort.direction;
    }
    return undefined;
  };

  useSort = () => {
    const snap = useSnapshot(this.sort);
    const res = useMemo(() => {
      return {
        ...snap,
        getSortDirection: this.getSortDirection,
      };
    }, [snap]);
    return res;
  };
}
